import { useState, useEffect } from "react";
import { FaBell, FaRegBell } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import axiosInstance from "../utils/axiosInstance";
import { formatDistanceToNow } from "date-fns";

const NotificationButton = ({ userId, notifications, setNotifications }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [seenNotificationIds, setSeenNotificationIds] = useState(new Set());

  const unseenCount =
    notifications?.filter((n) => !seenNotificationIds.has(n.id)).length || 0;

  const markNotificationsAsRead = async () => {
    try {
      setNotifications([]);
      await axiosInstance.put(`/notifications/users/${userId}/mark-read`);
      setSeenNotificationIds(new Set(notifications.map((n) => n.id)));
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  const markSingleNotificationAsRead = async (notificationId) => {
    try {
      await axiosInstance.put(
        `/notifications/users/${userId}/mark-read/${notificationId}`
      );
      setSeenNotificationIds((prev) => new Set(prev).add(notificationId));
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center space-x-2 p-2.5 rounded-full hover:bg-gray-200 bg-gray-200 dark:bg-gray-700 transition-all relative"
      >
        <FaBell />
        {unseenCount > 0 && (
          <span className="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
            {unseenCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 shadow-xl rounded-xl p-4 z-50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">
              Notifications
            </h3>
            {notifications.length > 0 && (
              <button
                onClick={markNotificationsAsRead}
                className="text-sm text-blue-600 hover:underline"
              >
                Mark all as read
              </button>
            )}
          </div>

          {notifications.length > 0 ? (
            <ul className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {notifications.map((n) => {
                const isRead = seenNotificationIds.has(n.id);
                return (
                  <li
                    key={n.id}
                    className={`relative flex items-start gap-3 p-3 rounded-lg border ${
                      isRead
                        ? "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                        : "bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-600"
                    }`}
                  >
                    <div className="mt-1 text-blue-500 dark:text-blue-300">
                      <FaRegBell />
                    </div>
                    <div className="flex flex-col w-full pr-6">
                      <div
                        className="text-sm font-medium text-gray-800 dark:text-white"
                        dangerouslySetInnerHTML={{ __html: n.message }}
                      />
                      {n.url && (
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            await markSingleNotificationAsRead(n.id);
                            window.location.href = n.url;
                          }}
                          className="mt-1 text-sm text-blue-600 hover:underline self-start"
                        >
                          View
                        </button>
                      )}
                      {n.date && (
                        <span className="text-xs text-gray-400 mt-1">
                          {formatDistanceToNow(new Date(n.date), {
                            addSuffix: true,
                          })}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => markSingleNotificationAsRead(n.id)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                      title="Mark as read"
                    >
                      <IoClose size={16} />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
              No new notifications
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationButton;
